import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/contexts/AuthContext';
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { sanitizeSupabaseError } from '@/lib/errorHandler';
import { useAnalytics } from '@/hooks/useAnalytics';
import {
  listPlans,
  createCheckoutSession,
  createPortalSession,
  getOrganizationPlan,
  type Plan,
} from '@/services/stripe-billing.service';

export type { Plan };

export function usePlans() {
  return useQuery({
    queryKey: ['plans'],
    queryFn: () => listPlans(),
  });
}

export function useStripeCheckout() {
  const { organization } = useAuthContext();
  const { track } = useAnalytics();

  return useMutation({
    mutationFn: async ({ price_id }: { price_id: string }) => {
      if (!organization?.id) throw new Error('Organização não encontrada');
      const result = await createCheckoutSession({ priceId: price_id, organizationId: organization.id });
      track('checkout_started', { price_id });
      return result;
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error) => {
      toast.error(sanitizeSupabaseError(error));
    },
  });
}

export function useStripePortal() {
  const { organization } = useAuthContext();

  return useMutation({
    mutationFn: async () => {
      if (!organization?.id) throw new Error('Organização não encontrada');
      return createPortalSession({ organizationId: organization.id });
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error) => {
      toast.error(sanitizeSupabaseError(error));
    },
  });
}

export function useCheckoutPolling() {
  const { organization } = useAuthContext();
  const { t } = useTranslation();
  const [isPolling, setIsPolling] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isTimeout, setIsTimeout] = useState(false);
  const [initialPlano, setInitialPlano] = useState<string | null>(null);

  const startPolling = useCallback(() => {
    if (!organization) return;
    setInitialPlano(organization.plano);
    setIsPolling(true);
    setIsSuccess(false);
    setIsTimeout(false);
  }, [organization]);

  useEffect(() => {
    if (!isPolling || !organization?.id || initialPlano === null) return;

    let attempts = 0;
    const maxAttempts = 15; // 15 * 2s = 30s

    const interval = setInterval(async () => {
      attempts++;

      try {
        const plano = await getOrganizationPlan(organization.id);

        if (plano && plano !== initialPlano) {
          clearInterval(interval);
          setIsPolling(false);
          setIsSuccess(true);
          toast.success(t('billing.planUpdated', { plan: plano === 'professional' ? 'Professional' : 'Business' }));
          // Reload to refresh auth context with new plan data
          setTimeout(() => window.location.reload(), 1500);
          return;
        }
      } catch {
        // Preserve legacy silent-error behavior: keep polling through transient failures.
        // Timeout will eventually fire if errors persist.
      }

      if (attempts >= maxAttempts) {
        clearInterval(interval);
        setIsPolling(false);
        setIsTimeout(true);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [isPolling, organization?.id, initialPlano, t]);

  return { isPolling, isSuccess, isTimeout, startPolling };
}
